/**
 * Helper to safely format numbers for display.
 */
const fmt = (val, decimals = 2) => {
  const num = Number(val);
  if (isNaN(num) || !Number.isFinite(num)) return '0';
  return num.toFixed(decimals);
};

const num = (val) => Number(val) || 0;

/**
 * Complete Metadata map with dynamic numeric evaluator functions for Data Entry.
 * Evaluates live numbers directly from useForm watch().
 */
export const CALC_ENTITY_METADATA = {
  // ─── BL DETAILS ───
  containersNumber: {
    title: 'CONTAINER NOS',
    category: 'BL DETAILS',
    description: 'Total container count for the shipment. Serves as key multiplier for all CFS & port charge totals.',
    formulaText: 'Total Charge = Per-Container Rate × Container Count',
    evaluate: (v) => {
      const count = num(v.containersNumber);
      const cfsNew = num(v.cfsChargesNew);
      const chaNew = num(v.chaSurveyNew);
      const freightNew = num(v.freightChargeNew);
      const netCbm = num(v.totalNetCbm) || 1;

      return {
        inputs: [
          { label: 'containersNumber', val: count },
          { label: 'cfsChargesNew', val: cfsNew },
          { label: 'chaSurveyNew', val: chaNew },
          { label: 'freightChargeNew', val: freightNew },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `CFS Total (${cfsNew} × ${count}) + CHA Total (${chaNew} × ${count}) + Freight (${freightNew} × ${count})`,
        steps: [
          { label: 'CFS Charges Total', expr: `${cfsNew} × ${count} = ${fmt(cfsNew * count)}` },
          { label: 'CHA Survey Total', expr: `${chaNew} × ${count} = ${fmt(chaNew * count)}` },
          { label: 'Freight Charge Total', expr: `${freightNew} × ${count} = ${fmt(freightNew * count)}` },
          { label: 'Yard Rent Allocation', expr: `(${count} × 3000) / ${fmt(netCbm)} / 35.315 = ${fmt((count * 3000) / netCbm / 35.315)}` },
        ],
        liveOutput: count,
        outputLabel: 'Active Container Count',
      };
    },
  },

  pqWeightcheck: {
    title: 'PQ WEIGHT CHECK',
    category: 'BL DETAILS',
    description: 'Plant Quarantine weight factor calculation.',
    formulaText: 'PQ Weight Invs = (pqWeightcheck - 1) × 200 + 3500',
    evaluate: (v) => {
      const check = num(v.pqWeightcheck);
      const netCbm = num(v.totalNetCbm) || 1;
      const invs = (check - 1) * 200 + 3500;
      const allocatedInt = (invs * 0.06) / netCbm / 35.315;

      return {
        inputs: [
          { label: 'pqWeightcheck', val: check },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `PQ Invs = (${check} - 1) × 200 + 3500 = ${invs}`,
        steps: [
          { label: 'Invoiced Base Value', expr: `(${check} - 1) × 200 + 3500 = ${invs}` },
          { label: '6% Interest @ Volume', expr: `(${invs} × 0.06) / ${fmt(netCbm)} / 35.315 = ${fmt(allocatedInt, 4)}` },
        ],
        liveOutput: fmt(allocatedInt, 4),
        outputLabel: 'Interest Contribution (totalInt)',
      };
    },
  },

  pqWeight: {
    title: 'PQ WEIGHT 4*',
    category: 'BL DETAILS',
    description: 'Plant Quarantine weight 4x multiplier for total interest & CFT allocation.',
    formulaText: 'PQ Weight 4x = (pqWeight × 6%) / totalNetCbm / 35.315',
    evaluate: (v) => {
      const weight = num(v.pqWeight);
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;
      const intVal = (weight * 0.06) / cftVolume;
      const cftVal = weight / cftVolume;

      return {
        inputs: [
          { label: 'pqWeight', val: weight },
          { label: 'totalNetCbm', val: fmt(netCbm) },
          { label: 'CFT Volume', val: fmt(cftVolume) },
        ],
        substitutedFormula: `(${weight} × 0.06) / (${fmt(netCbm)} × 35.315)`,
        steps: [
          { label: 'Base Weight', expr: `${weight}` },
          { label: '6% Interest Amount', expr: `${weight} × 0.06 = ${fmt(weight * 0.06)}` },
          { label: 'Total Volume (CFT)', expr: `${fmt(netCbm)} × 35.315 = ${fmt(cftVolume)}` },
          { label: 'Interest Per CFT', expr: `${fmt(weight * 0.06)} / ${fmt(cftVolume)} = ${fmt(intVal, 4)}` },
          { label: 'Base CFT Rate', expr: `${weight} / ${fmt(cftVolume)} = ${fmt(cftVal, 4)}` },
        ],
        liveOutput: fmt(intVal, 4),
        outputLabel: 'Interest Contribution (totalInt)',
      };
    },
  },

  pqFumigation: {
    title: 'PQ RELAX / FUMIGATION',
    category: 'BL DETAILS',
    description: 'PQ Fumigation relax charge factor allocation per CFT.',
    formulaText: 'PQ Fumigation Val = (pqFumigation × 0 × 6%) / totalNetCbm / 35.315',
    evaluate: (v) => {
      const fum = num(v.pqFumigation);
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;
      const intVal = 0;
      const cftVal = fum / cftVolume;

      return {
        inputs: [
          { label: 'pqFumigation', val: fum },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `(${fum} × 0 × 0.06) / (${fmt(netCbm)} × 35.315)`,
        steps: [
          { label: 'Relax Value', expr: `${fum}` },
          { label: 'Interest Multiplier', expr: `0 (Interest exempt in current rules)` },
          { label: 'Interest Per CFT', expr: `0` },
          { label: 'Base CFT Rate', expr: `${fum} / ${fmt(cftVolume)} = ${fmt(cftVal, 4)}` },
        ],
        liveOutput: fmt(intVal, 4),
        outputLabel: 'Interest Contribution (totalInt)',
      };
    },
  },

  fumigationCharge: {
    title: 'FUMIGATION CHARGE',
    category: 'SHIP & CH',
    description: 'Fumigation charges with 18% GST and 6% interest allocation.',
    formulaText: 'Fumigation Interest = (fumigationCharge × 1.18 × 6%) / totalNetCbm / 35.315',
    evaluate: (v) => {
      const charge = num(v.fumigationCharge);
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;
      const gst = charge * 0.18;
      const invs = charge + gst;
      const intVal = (invs * 0.06) / cftVolume;
      const cftVal = invs / cftVolume;

      return {
        inputs: [
          { label: 'fumigationCharge', val: charge },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `(${charge} × 1.18 × 0.06) / (${fmt(netCbm)} × 35.315)`,
        steps: [
          { label: 'GST (18%)', expr: `${charge} × 0.18 = ${fmt(gst)}` },
          { label: 'Total Invoiced Fumigation', expr: `${charge} + ${fmt(gst)} = ${fmt(invs)}` },
          { label: '6% Interest Amount', expr: `${fmt(invs)} × 0.06 = ${fmt(invs * 0.06)}` },
          { label: 'Interest Per CFT', expr: `${fmt(invs * 0.06)} / ${fmt(cftVolume)} = ${fmt(intVal, 4)}` },
          { label: 'Base Fumigation CFT Rate', expr: `${fmt(invs)} / ${fmt(cftVolume)} = ${fmt(cftVal, 4)}` },
        ],
        liveOutput: fmt(intVal, 4),
        outputLabel: 'Interest Contribution (totalInt)',
      };
    },
  },

  // ─── INVOICE DETAILS ───
  sblcInterestUsdRate: {
    title: 'COST USD RATE (SBLC INT USD RATE)',
    category: 'INV DETAILS',
    description: 'SBLC interest USD rate used to compute USD-denominated interest per CFT.',
    formulaText: 'SBLC Interest USD = (sblcInterestCharges × sblcInterestUsdRate) / totalNetCbm / 35.315',
    evaluate: (v) => {
      const charges = num(v.sblcInterestCharges);
      const rate = num(v.sblcInterestUsdRate);
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;
      const totalUsdCost = charges * rate;
      const cftRate = totalUsdCost / cftVolume;

      return {
        inputs: [
          { label: 'sblcInterestCharges', val: charges },
          { label: 'sblcInterestUsdRate', val: rate },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `(${charges} × ${rate}) / (${fmt(netCbm)} × 35.315)`,
        steps: [
          { label: 'Total USD Interest Cost', expr: `${charges} × ${rate} = ${fmt(totalUsdCost)}` },
          { label: 'Volume (CFT)', expr: `${fmt(netCbm)} × 35.315 = ${fmt(cftVolume)}` },
          { label: 'SBLC Rate Per CFT', expr: `${fmt(totalUsdCost)} / ${fmt(cftVolume)} = ${fmt(cftRate, 4)}` },
        ],
        liveOutput: fmt(cftRate, 4),
        outputLabel: 'CFT Rate Contribution (totalCft)',
      };
    },
  },

  bgAmtInterestAmt: {
    title: 'INVOICE AMOUNT',
    category: 'INV DETAILS',
    description: 'Total Invoice / BG Amount & Net Invoice Amount.',
    formulaText: 'Total Invoice Amt = totalAmount - roundOff',
    evaluate: (v) => {
      const totAmt = num(v.totalAmount);
      const ro = num(v.roundOff);
      const netInv = totAmt - ro;

      return {
        inputs: [
          { label: 'totalAmount', val: fmt(totAmt) },
          { label: 'roundOff', val: ro },
        ],
        substitutedFormula: `${fmt(totAmt)} - ${ro}`,
        steps: [
          { label: 'Gross Summary Amount', expr: `${fmt(totAmt)}` },
          { label: 'Subtract Round Off', expr: `- ${ro}` },
          { label: 'Final Net Invoice', expr: `${fmt(netInv)}` },
        ],
        liveOutput: fmt(netInv),
        outputLabel: 'Total Invoice Amount',
      };
    },
  },

  // ─── BANK DETAILS ───
  bgCharges: {
    title: 'BG CHARGES',
    category: 'BANK DETAILS',
    description: 'Bank Guarantee charges with 18% GST and 6% interest allocation.',
    formulaText: 'BG Interest = (bgCharges × 1.18 × 6%) / totalNetCbm / 35.315',
    evaluate: (v) => {
      const bg = num(v.bgCharges);
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;
      const gst = bg * 0.18;
      const bgInvs = bg + gst;
      const bgVal = (bgInvs * 0.06) / cftVolume;
      const bgCft = bg / cftVolume;

      return {
        inputs: [
          { label: 'bgCharges', val: bg },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `(${bg} × 1.18 × 0.06) / (${fmt(netCbm)} × 35.315)`,
        steps: [
          { label: 'GST (18%)', expr: `${bg} × 0.18 = ${fmt(gst)}` },
          { label: 'Total Invoiced BG', expr: `${bg} + ${fmt(gst)} = ${fmt(bgInvs)}` },
          { label: '6% Interest Amount', expr: `${fmt(bgInvs)} × 0.06 = ${fmt(bgInvs * 0.06)}` },
          { label: 'Interest Per CFT', expr: `${fmt(bgInvs * 0.06)} / ${fmt(cftVolume)} = ${fmt(bgVal, 4)}` },
          { label: 'Base BG CFT Rate', expr: `${bg} / ${fmt(cftVolume)} = ${fmt(bgCft, 4)}` },
        ],
        liveOutput: fmt(bgVal, 4),
        outputLabel: 'Interest Contribution (totalInt)',
      };
    },
  },

  upFrontCharges: {
    title: 'UPFRONT CHARGES',
    category: 'BANK DETAILS',
    description: 'Upfront bank charges with 18% GST and 6% interest allocation.',
    formulaText: 'Upfront Interest = (upFrontCharges × 1.18 × 6%) / totalNetCbm / 35.315',
    evaluate: (v) => {
      const upfront = num(v.upFrontCharges);
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;
      const gst = upfront * 0.18;
      const invs = upfront + gst;
      const intVal = (invs * 0.06) / cftVolume;

      return {
        inputs: [
          { label: 'upFrontCharges', val: upfront },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `(${upfront} × 1.18 × 0.06) / (${fmt(netCbm)} × 35.315)`,
        steps: [
          { label: 'GST (18%)', expr: `${upfront} × 0.18 = ${fmt(gst)}` },
          { label: 'Total Upfront Invoice', expr: `${upfront} + ${fmt(gst)} = ${fmt(invs)}` },
          { label: '6% Interest Amount', expr: `${fmt(invs)} × 0.06 = ${fmt(invs * 0.06)}` },
          { label: 'Interest Per CFT', expr: `${fmt(invs * 0.06)} / ${fmt(cftVolume)} = ${fmt(intVal, 4)}` },
        ],
        liveOutput: fmt(intVal, 4),
        outputLabel: 'Interest Contribution (totalInt)',
      };
    },
  },

  margin10: {
    title: 'MARGIN 10 (M1 VALUE)',
    category: 'BANK DETAILS',
    description: '10% Margin interest calculation at 2.5% rate.',
    formulaText: 'Margin 10 Val = (margin10 × 2.5%) / totalNetCbm / 35.315',
    evaluate: (v) => {
      const m1 = num(v.margin10);
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;
      const factor = m1 * 0.025;
      const val = factor / cftVolume;

      return {
        inputs: [
          { label: 'margin10', val: m1 },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `(${m1} × 0.025) / (${fmt(netCbm)} × 35.315)`,
        steps: [
          { label: '2.5% Interest Factor', expr: `${m1} × 0.025 = ${fmt(factor)}` },
          { label: 'Allocated Value', expr: `${fmt(factor)} / ${fmt(cftVolume)} = ${fmt(val, 4)}` },
        ],
        liveOutput: fmt(val, 4),
        outputLabel: 'Interest Contribution (totalInt)',
      };
    },
  },

  margin5: {
    title: 'MARGIN 5 (M2 VALUE)',
    category: 'BANK DETAILS',
    description: '5% Margin interest calculation at 2.5% rate.',
    formulaText: 'Margin 5 Val = (margin5 × 2.5%) / totalNetCbm / 35.315',
    evaluate: (v) => {
      const m2 = num(v.margin5);
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;
      const factor = m2 * 0.025;
      const val = factor / cftVolume;

      return {
        inputs: [
          { label: 'margin5', val: m2 },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `(${m2} × 0.025) / (${fmt(netCbm)} × 35.315)`,
        steps: [
          { label: '2.5% Interest Factor', expr: `${m2} × 0.025 = ${fmt(factor)}` },
          { label: 'Allocated Value', expr: `${fmt(factor)} / ${fmt(cftVolume)} = ${fmt(val, 4)}` },
        ],
        liveOutput: fmt(val, 4),
        outputLabel: 'Interest Contribution (totalInt)',
      };
    },
  },

  advice: {
    title: 'ADVICE & ADVICE GST',
    category: 'BANK DETAILS',
    description: 'Bank Advice charge plus GST with 6% interest allocation.',
    formulaText: 'Advice Val = ((advice + adviceGst) × 6%) / totalNetCbm / 35.315',
    evaluate: (v) => {
      const adv = num(v.advice);
      const advGst = num(v.adviceGst);
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;
      const totalAdv = adv + advGst;
      const val = (totalAdv * 0.06) / cftVolume;

      return {
        inputs: [
          { label: 'advice', val: adv },
          { label: 'adviceGst', val: advGst },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `((${adv} + ${advGst}) × 0.06) / (${fmt(netCbm)} × 35.315)`,
        steps: [
          { label: 'Total Advice Cost', expr: `${adv} + ${advGst} = ${fmt(totalAdv)}` },
          { label: '6% Interest Amount', expr: `${fmt(totalAdv)} × 0.06 = ${fmt(totalAdv * 0.06)}` },
          { label: 'Allocated Interest', expr: `${fmt(totalAdv * 0.06)} / ${fmt(cftVolume)} = ${fmt(val, 4)}` },
        ],
        liveOutput: fmt(val, 4),
        outputLabel: 'Interest Contribution (totalInt)',
      };
    },
  },

  // ─── BE DETAILS ───
  customDuty: {
    title: 'CUSTOM DUTY',
    category: 'BE DETAILS',
    description: 'Custom Duty charges with 6% interest allocation.',
    formulaText: 'Custom Duty Val = (customDuty × 6%) / totalNetCbm / 35.315',
    evaluate: (v) => {
      const duty = num(v.customDuty);
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;
      const intVal = (duty * 0.06) / cftVolume;
      const cftVal = duty / cftVolume;

      return {
        inputs: [
          { label: 'customDuty', val: duty },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `(${duty} × 0.06) / (${fmt(netCbm)} × 35.315)`,
        steps: [
          { label: 'Base Custom Duty', expr: `${duty}` },
          { label: '6% Interest Amount', expr: `${duty} × 0.06 = ${fmt(duty * 0.06)}` },
          { label: 'Interest Per CFT', expr: `${fmt(duty * 0.06)} / ${fmt(cftVolume)} = ${fmt(intVal, 4)}` },
          { label: 'Base CFT Rate', expr: `${duty} / ${fmt(cftVolume)} = ${fmt(cftVal, 4)}` },
        ],
        liveOutput: fmt(intVal, 4),
        outputLabel: 'Interest Contribution (totalInt)',
      };
    },
  },

  socialWelfareCharges: {
    title: 'SOCIAL WELFARE CHARGES',
    category: 'BE DETAILS',
    description: 'Social Welfare charges with 6% interest allocation.',
    formulaText: 'Social Welfare Val = (socialWelfareCharges × 6%) / totalNetCbm / 35.315',
    evaluate: (v) => {
      const sw = num(v.socialWelfareCharges);
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;
      const intVal = (sw * 0.06) / cftVolume;

      return {
        inputs: [
          { label: 'socialWelfareCharges', val: sw },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `(${sw} × 0.06) / (${fmt(netCbm)} × 35.315)`,
        steps: [
          { label: 'Base Charge', expr: `${sw}` },
          { label: '6% Interest Amount', expr: `${sw} × 0.06 = ${fmt(sw * 0.06)}` },
          { label: 'Interest Per CFT', expr: `${fmt(sw * 0.06)} / ${fmt(cftVolume)} = ${fmt(intVal, 4)}` },
        ],
        liveOutput: fmt(intVal, 4),
        outputLabel: 'Interest Contribution (totalInt)',
      };
    },
  },

  // ─── SHIP & CH DETAILS ───
  shippingBill: {
    title: 'SHIPPING BILL',
    category: 'SHIP & CH',
    description: 'Shipping Bill charges with 18% GST and 6% interest allocation.',
    formulaText: 'Shipping Bill Interest = (shippingBill × 1.18 × 6%) / totalNetCbm / 35.315',
    evaluate: (v) => {
      const sb = num(v.shippingBill);
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;
      const gst = sb * 0.18;
      const sbInvs = sb + gst;
      const intVal = (sbInvs * 0.06) / cftVolume;

      return {
        inputs: [
          { label: 'shippingBill', val: sb },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `(${sb} × 1.18 × 0.06) / (${fmt(netCbm)} × 35.315)`,
        steps: [
          { label: 'GST (18%)', expr: `${sb} × 0.18 = ${fmt(gst)}` },
          { label: 'Total Invoiced Shipping Bill', expr: `${sb} + ${fmt(gst)} = ${fmt(sbInvs)}` },
          { label: '6% Interest Amount', expr: `${fmt(sbInvs)} × 0.06 = ${fmt(sbInvs * 0.06)}` },
          { label: 'Interest Per CFT', expr: `${fmt(sbInvs * 0.06)} / ${fmt(cftVolume)} = ${fmt(intVal, 4)}` },
        ],
        liveOutput: fmt(intVal, 4),
        outputLabel: 'Interest Contribution (totalInt)',
      };
    },
  },

  cfsChargesNew: {
    title: 'CFS CHARGES (PER CONT.)',
    category: 'SHIP & CH',
    description: 'Per-container CFS charge rate.',
    formulaText: 'cfsCharges = cfsChargesNew × containersNumber',
    evaluate: (v) => {
      const cfsNew = num(v.cfsChargesNew);
      const count = num(v.containersNumber);
      const cfsTot = cfsNew * count;
      const gst = cfsTot * 0.18;
      const invs = cfsTot + gst;
      const netCbm = num(v.totalNetCbm) || 1;
      const intVal = (invs * 0.06) / (netCbm * 35.315);

      return {
        inputs: [
          { label: 'cfsChargesNew', val: cfsNew },
          { label: 'containersNumber', val: count },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `${cfsNew} × ${count} = ${fmt(cfsTot)}`,
        steps: [
          { label: 'Total CFS Charges', expr: `${cfsNew} × ${count} = ${fmt(cfsTot)}` },
          { label: 'GST (18%)', expr: `${fmt(cfsTot)} × 0.18 = ${fmt(gst)}` },
          { label: 'Total Invoiced CFS', expr: `${fmt(cfsTot)} + ${fmt(gst)} = ${fmt(invs)}` },
          { label: '6% Interest Allocation', expr: `(${fmt(invs)} × 0.06) / (${fmt(netCbm)} × 35.315) = ${fmt(intVal, 4)}` },
          { label: 'Base CFS CFT Rate', expr: `${fmt(cfsTot)} / (${fmt(netCbm)} × 35.315) = ${fmt(cfsTot / (netCbm * 35.315), 4)}` },
        ],
        liveOutput: fmt(cfsTot),
        outputLabel: 'Total CFS Charges (cfsCharges)',
      };
    },
  },

  chaSurveyNew: {
    title: 'CHA SURVEY (PER CONT.)',
    category: 'SHIP & CH',
    description: 'Per-container CHA Survey rate.',
    formulaText: 'chaSurvey = chaSurveyNew × containersNumber',
    evaluate: (v) => {
      const chaNew = num(v.chaSurveyNew);
      const count = num(v.containersNumber);
      const chaTot = chaNew * count;
      const gst = chaTot * 0.18;
      const invs = chaTot + gst;
      const netCbm = num(v.totalNetCbm) || 1;
      const intVal = (invs * 0.06) / (netCbm * 35.315);

      return {
        inputs: [
          { label: 'chaSurveyNew', val: chaNew },
          { label: 'containersNumber', val: count },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `${chaNew} × ${count} = ${fmt(chaTot)}`,
        steps: [
          { label: 'Total CHA Survey', expr: `${chaNew} × ${count} = ${fmt(chaTot)}` },
          { label: 'GST (18%)', expr: `${fmt(chaTot)} × 0.18 = ${fmt(gst)}` },
          { label: 'Total Invoiced CHA', expr: `${fmt(chaTot)} + ${fmt(gst)} = ${fmt(invs)}` },
          { label: '6% Interest Allocation', expr: `(${fmt(invs)} × 0.06) / (${fmt(netCbm)} × 35.315) = ${fmt(intVal, 4)}` },
          { label: 'Base CHA CFT Rate', expr: `${fmt(chaTot)} / (${fmt(netCbm)} × 35.315) = ${fmt(chaTot / (netCbm * 35.315), 4)}` },
        ],
        liveOutput: fmt(chaTot),
        outputLabel: 'Total CHA Survey (chaSurvey)',
      };
    },
  },

  examinationChargenew: {
    title: 'EXAMINATION CHARGE (PER CONT.)',
    category: 'SHIP & CH',
    description: 'Per-container Examination charge rate.',
    formulaText: 'examinationCharge = examinationChargenew × containersNumber',
    evaluate: (v) => {
      const examNew = num(v.examinationChargenew);
      const count = num(v.containersNumber);
      const examTot = examNew * count;
      const gst = examTot * 0.18;
      const invs = examTot + gst;
      const netCbm = num(v.totalNetCbm) || 1;
      const intVal = (invs * 0.06) / (netCbm * 35.315);

      return {
        inputs: [
          { label: 'examinationChargenew', val: examNew },
          { label: 'containersNumber', val: count },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `${examNew} × ${count} = ${fmt(examTot)}`,
        steps: [
          { label: 'Total Exam Charge', expr: `${examNew} × ${count} = ${fmt(examTot)}` },
          { label: 'GST (18%)', expr: `${fmt(examTot)} × 0.18 = ${fmt(gst)}` },
          { label: 'Total Invoiced Exam', expr: `${fmt(examTot)} + ${fmt(gst)} = ${fmt(invs)}` },
          { label: '6% Interest Allocation', expr: `(${fmt(invs)} × 0.06) / (${fmt(netCbm)} × 35.315) = ${fmt(intVal, 4)}` },
          { label: 'Base Exam CFT Rate', expr: `${fmt(examTot)} / (${fmt(netCbm)} × 35.315) = ${fmt(examTot / (netCbm * 35.315), 4)}` },
        ],
        liveOutput: fmt(examTot),
        outputLabel: 'Total Exam Charge (examinationCharge)',
      };
    },
  },

  cfsGroundRentNew: {
    title: 'CFS GROUND RENT (PER CONT.)',
    category: 'SHIP & CH',
    description: 'Per-container CFS Ground Rent rate.',
    formulaText: 'cfsGroundRent = cfsGroundRentNew × containersNumber',
    evaluate: (v) => {
      const groundNew = num(v.cfsGroundRentNew);
      const count = num(v.containersNumber);
      const groundTot = groundNew * count;
      const gst = groundTot * 0.18;
      const invs = groundTot + gst;
      const netCbm = num(v.totalNetCbm) || 1;
      const intVal = (invs * 0.06) / (netCbm * 35.315);

      return {
        inputs: [
          { label: 'cfsGroundRentNew', val: groundNew },
          { label: 'containersNumber', val: count },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `${groundNew} × ${count} = ${fmt(groundTot)}`,
        steps: [
          { label: 'Total Ground Rent', expr: `${groundNew} × ${count} = ${fmt(groundTot)}` },
          { label: 'GST (18%)', expr: `${fmt(groundTot)} × 0.18 = ${fmt(gst)}` },
          { label: 'Total Invoiced Ground', expr: `${fmt(groundTot)} + ${fmt(gst)} = ${fmt(invs)}` },
          { label: '6% Interest Allocation', expr: `(${fmt(invs)} × 0.06) / (${fmt(netCbm)} × 35.315) = ${fmt(intVal, 4)}` },
          { label: 'Base Ground CFT Rate', expr: `${fmt(groundTot)} / (${fmt(netCbm)} × 35.315) = ${fmt(groundTot / (netCbm * 35.315), 4)}` },
        ],
        liveOutput: fmt(groundTot),
        outputLabel: 'Total CFS Ground Rent',
      };
    },
  },

  freightChargeNew: {
    title: 'FREIGHT CHARGE (PER CONT.)',
    category: 'SHIP & CH',
    description: 'Per-container Freight charge rate with 5% GST.',
    formulaText: 'freightCharge = freightChargeNew × containersNumber',
    evaluate: (v) => {
      const frtNew = num(v.freightChargeNew);
      const count = num(v.containersNumber);
      const frtTot = frtNew * count;
      const gst = frtTot * 0.05;
      const invs = frtTot + gst;
      const netCbm = num(v.totalNetCbm) || 1;
      const intVal = (invs * 0.06) / (netCbm * 35.315);

      return {
        inputs: [
          { label: 'freightChargeNew', val: frtNew },
          { label: 'containersNumber', val: count },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `${frtNew} × ${count} = ${fmt(frtTot)}`,
        steps: [
          { label: 'Total Freight Charge', expr: `${frtNew} × ${count} = ${fmt(frtTot)}` },
          { label: '5% Freight GST', expr: `${fmt(frtTot)} × 0.05 = ${fmt(gst)}` },
          { label: 'Invoiced Freight Total', expr: `${fmt(frtTot)} + ${fmt(gst)} = ${fmt(invs)}` },
          { label: '6% Interest Allocation', expr: `(${fmt(invs)} × 0.06) / (${fmt(netCbm)} × 35.315) = ${fmt(intVal, 4)}` },
          { label: 'Base Freight CFT Rate', expr: `${fmt(frtTot)} / (${fmt(netCbm)} × 35.315) = ${fmt(frtTot / (netCbm * 35.315), 4)}` },
        ],
        liveOutput: fmt(frtTot),
        outputLabel: 'Total Freight Charge (freightCharge)',
      };
    },
  },

  unloadingNew: {
    title: 'UNLOADING CHARGE (PER CONT.)',
    category: 'SHIP & CH',
    description: 'Per-container Unloading rate with 18% GST.',
    formulaText: 'unLoading = unloadingNew × containersNumber',
    evaluate: (v) => {
      const unloadNew = num(v.unloadingNew);
      const count = num(v.containersNumber);
      const unloadTot = unloadNew * count;
      const gst = unloadTot * 0.18;
      const invs = unloadTot + gst;
      const netCbm = num(v.totalNetCbm) || 1;
      const intVal = (invs * 0.06) / (netCbm * 35.315);

      return {
        inputs: [
          { label: 'unloadingNew', val: unloadNew },
          { label: 'containersNumber', val: count },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `${unloadNew} × ${count} = ${fmt(unloadTot)}`,
        steps: [
          { label: 'Total Unloading', expr: `${unloadNew} × ${count} = ${fmt(unloadTot)}` },
          { label: 'GST (18%)', expr: `${fmt(unloadTot)} × 0.18 = ${fmt(gst)}` },
          { label: 'Total Invoiced Unloading', expr: `${fmt(unloadTot)} + ${fmt(gst)} = ${fmt(invs)}` },
          { label: '6% Interest Allocation', expr: `(${fmt(invs)} × 0.06) / (${fmt(netCbm)} × 35.315) = ${fmt(intVal, 4)}` },
          { label: 'Base Unloading CFT Rate', expr: `${fmt(unloadTot)} / (${fmt(netCbm)} × 35.315) = ${fmt(unloadTot / (netCbm * 35.315), 4)}` },
        ],
        liveOutput: fmt(unloadTot),
        outputLabel: 'Total Unloading (unLoading)',
      };
    },
  },

  yardRentNew: {
    title: 'YARD RENT (PER CONT.)',
    category: 'SHIP & CH',
    description: 'Per-container Yard Rent / Extra Expenses.',
    formulaText: 'extraExpenses = yardRentNew × containersNumber',
    evaluate: (v) => {
      const yardNew = num(v.yardRentNew);
      const count = num(v.containersNumber);
      const yardTot = yardNew * count;
      const gst = yardTot * 0.18;
      const invs = yardTot + gst;
      const netCbm = num(v.totalNetCbm) || 1;
      const intVal = (invs * 0.06) / (netCbm * 35.315);

      return {
        inputs: [
          { label: 'yardRentNew', val: yardNew },
          { label: 'containersNumber', val: count },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `${yardNew} × ${count} = ${fmt(yardTot)}`,
        steps: [
          { label: 'Total Extra Expenses', expr: `${yardNew} × ${count} = ${fmt(yardTot)}` },
          { label: 'GST (18%)', expr: `${fmt(yardTot)} × 0.18 = ${fmt(gst)}` },
          { label: 'Total Invoiced Extra Expenses', expr: `${fmt(yardTot)} + ${fmt(gst)} = ${fmt(invs)}` },
          { label: '6% Interest Allocation', expr: `(${fmt(invs)} × 0.06) / (${fmt(netCbm)} × 35.315) = ${fmt(intVal, 4)}` },
          { label: 'Base Extra Expenses CFT Rate', expr: `${fmt(yardTot)} / (${fmt(netCbm)} × 35.315) = ${fmt(yardTot / (netCbm * 35.315), 4)}` },
        ],
        liveOutput: fmt(yardTot),
        outputLabel: 'Total Extra Expenses (extraExpenses)',
      };
    },
  },

  sec49New: {
    title: 'SEC 49 (PER CONT.)',
    category: 'SHIP & CH',
    description: 'Per-container Sec 49 charge rate.',
    formulaText: 'sec49 = sec49New × containersNumber',
    evaluate: (v) => {
      const secNew = num(v.sec49New);
      const count = num(v.containersNumber);
      const secTot = secNew * count;
      const gst = secTot * 0.18;
      const invs = secTot + gst;
      const netCbm = num(v.totalNetCbm) || 1;
      const intVal = (invs * 0.06) / (netCbm * 35.315);

      return {
        inputs: [
          { label: 'sec49New', val: secNew },
          { label: 'containersNumber', val: count },
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `${secNew} × ${count} = ${fmt(secTot)}`,
        steps: [
          { label: 'Total Sec 49 Charge', expr: `${secNew} × ${count} = ${fmt(secTot)}` },
          { label: 'GST (18%)', expr: `${fmt(secTot)} × 0.18 = ${fmt(gst)}` },
          { label: 'Total Invoiced Sec 49', expr: `${fmt(secTot)} + ${fmt(gst)} = ${fmt(invs)}` },
          { label: '6% Interest Allocation', expr: `(${fmt(invs)} × 0.06) / (${fmt(netCbm)} × 35.315) = ${fmt(intVal, 4)}` },
          { label: 'Base Sec 49 CFT Rate', expr: `${fmt(secTot)} / (${fmt(netCbm)} × 35.315) = ${fmt(secTot / (netCbm * 35.315), 4)}` },
        ],
        liveOutput: fmt(secTot),
        outputLabel: 'Sec 49 Total',
      };
    },
  },

  totalCft: {
    title: 'TOTAL CFT',
    category: 'SHIP & CH',
    description: 'Sum of all allocated CFT components per unit volume.',
    formulaText: 'totalCft = Sum of 19 CFT Rates',
    evaluate: (v) => {
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;
      const isCredit = String(v.paymentOption || 'Credit').toLowerCase() === 'credit';
      const sblcInterestChargesVal = isCredit
        ? Number((num(v.sblcInterestCharges) * num(v.sblcInterestUsdRate)) / cftVolume).toFixed(2)
        : '0.00';
      const bgChargesCft = isCredit
        ? Number(num(v.bgCharges) / cftVolume).toFixed(2)
        : '0.00';
      const upFrontChargesCft = isCredit
        ? Number(num(v.upFrontCharges) / cftVolume).toFixed(2)
        : '0.00';
      const adviceCft = Number(num(v.advice) / cftVolume).toFixed(4);
      const customDutyCft = Number(num(v.customDuty) / cftVolume).toFixed(2);
      const socialWelfareChargeCft = Number(num(v.socialWelfareCharges) / cftVolume).toFixed(2);
      const shippingBillCft = Number(num(v.shippingBill) / cftVolume).toFixed(2);
      
      const pqWeightInvs = (num(v.pqWeightcheck) - 1) * 200 + 3500;
      const pqWeightCft = Number(pqWeightInvs / cftVolume).toFixed(2);
      
      const pqFumigationCft = Number(num(v.pqFumigation) / cftVolume).toFixed(2);
      
      const fumigationChargeInvs = num(v.fumigationCharge) * 1.18;
      const fumigationChargesCft = Number(fumigationChargeInvs / cftVolume).toFixed(2);

      const count = num(v.containersNumber);
      const cfsChargesCft = Number((num(v.cfsChargesNew) * count) / cftVolume).toFixed(2);
      const chaSurveyCft = Number((num(v.chaSurveyNew) * count) / cftVolume).toFixed(2);
      const freightChargeCft = Number((num(v.freightChargeNew) * count) / cftVolume).toFixed(2);
      const unLoadingCft = Number((num(v.unloadingNew) * count) / cftVolume).toFixed(2);
      const extraExpensesCft = Number((num(v.yardRentNew) * count) / cftVolume).toFixed(2);
      
      const examinationChargeCft = Number((num(v.examinationChargenew) * count) / cftVolume).toFixed(2);
      const sec49ChargeCft = Number((num(v.sec49New) * count) / cftVolume).toFixed(2);
      const cfsGroundChargeCft = Number((num(v.cfsGroundRentNew) * count) / cftVolume).toFixed(2);
      
      const pqweight4timesCft = Number(num(v.pqWeight) / cftVolume).toFixed(4);

      const totalCftVal = [
        sblcInterestChargesVal, bgChargesCft, upFrontChargesCft, adviceCft,
        customDutyCft, socialWelfareChargeCft, shippingBillCft, pqWeightCft,
        pqFumigationCft, fumigationChargesCft, cfsChargesCft, chaSurveyCft,
        freightChargeCft, unLoadingCft, extraExpensesCft, examinationChargeCft,
        sec49ChargeCft, cfsGroundChargeCft, pqweight4timesCft,
      ].reduce((sum, val) => sum + (Number(val) || 0), 0);

      return {
        inputs: [
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `${sblcInterestChargesVal} + ${bgChargesCft} + ${upFrontChargesCft} + ${adviceCft} + ${customDutyCft} + ${socialWelfareChargeCft} + ${shippingBillCft} + ${pqWeightCft} + ${pqFumigationCft} + ${fumigationChargesCft} + ${cfsChargesCft} + ${chaSurveyCft} + ${freightChargeCft} + ${unLoadingCft} + ${extraExpensesCft} + ${examinationChargeCft} + ${sec49ChargeCft} + ${cfsGroundChargeCft} + ${pqweight4timesCft}`,
        steps: [
          { label: '1. SBLC Interest (sblcInterestChargesVal)', expr: sblcInterestChargesVal },
          { label: '2. BG Charges CFT (bgChargesCft)', expr: bgChargesCft },
          { label: '3. Upfront Charges CFT (upFrontChargesCft)', expr: upFrontChargesCft },
          { label: '4. Advice Charges CFT (adviceCft)', expr: adviceCft },
          { label: '5. Custom Duty CFT (customDutyCft)', expr: customDutyCft },
          { label: '6. Social Welfare CFT (socialWelfareChargeCft)', expr: socialWelfareChargeCft },
          { label: '7. Shipping Bill CFT (shippingBillCft)', expr: shippingBillCft },
          { label: '8. PQ Weight Check CFT (pqWeightCft)', expr: pqWeightCft },
          { label: '9. PQ Fumigation Relaxation CFT (pqFumigationCft)', expr: pqFumigationCft },
          { label: '10. Fumigation Charges CFT (fumigationChargesCft)', expr: fumigationChargesCft },
          { label: '11. CFS Charges CFT (cfsChargesCft)', expr: cfsChargesCft },
          { label: '12. CHA Survey CFT (chaSurveyCft)', expr: chaSurveyCft },
          { label: '13. Freight Charges CFT (freightChargeCft)', expr: freightChargeCft },
          { label: '14. Unloading Charges CFT (unLoadingCft)', expr: unLoadingCft },
          { label: '15. Yard Rent / Extra Exp CFT (extraExpensesCft)', expr: extraExpensesCft },
          { label: '16. Examination Charges CFT (examinationChargeCft)', expr: examinationChargeCft },
          { label: '17. Sec 49 Charges CFT (sec49ChargeCft)', expr: sec49ChargeCft },
          { label: '18. CFS Ground Rent CFT (cfsGroundChargeCft)', expr: cfsGroundChargeCft },
          { label: '19. PQ Weight 4x CFT (pqweight4timesCft)', expr: pqweight4timesCft },
          { label: 'Final Calculated Sum', expr: fmt(totalCftVal, 2) }
        ],
        liveOutput: fmt(totalCftVal, 2),
        outputLabel: 'Total CFT Rate',
      };
    },
  },

  totalInt: {
    title: 'TOTAL INT',
    category: 'SHIP & CH',
    description: 'Sum of all 21 interest component allocations across shipment capacity.',
    formulaText: 'totalInt = Sum of 21 Interest Components',
    evaluate: (v) => {
      const netCbm = num(v.totalNetCbm) || 1;
      const cftVolume = netCbm * 35.315;

      const bgval = Number((num(v.bgCharges) * 1.18 * 0.06) / cftVolume).toFixed(2);
      const fronval = Number((num(v.upFrontCharges) * 1.18 * 0.06) / cftVolume).toFixed(2);
      const advisval = Number(((num(v.advice) + num(v.adviceGst)) * 0.06) / cftVolume).toFixed(2);
      const marginval = Number((num(v.margin10) * 0.025) / cftVolume).toFixed(2);
      const marginvalfive = Number((num(v.margin5) * 0.025) / cftVolume).toFixed(2);
      const customDutyval = Number((num(v.customDuty) * 0.06) / cftVolume).toFixed(2);
      const socialWelfareChargeval = Number((num(v.socialWelfareCharges) * 0.06) / cftVolume).toFixed(2);
      const shippingBillval = Number((num(v.shippingBill) * 1.18 * 0.06) / cftVolume).toFixed(2);
      const pqWeightval = Number((((num(v.pqWeightcheck) - 1) * 200 + 3500) * 0.06) / cftVolume).toFixed(2);
      const pqFumigationVal = Number((num(v.pqFumigation) * 0 * 0.06) / cftVolume).toFixed(2);
      const fumigationChargeVal = Number((num(v.fumigationCharge) * 1.18 * 0.06) / cftVolume).toFixed(2);
      
      const count = num(v.containersNumber);
      const cfsChargesVal = Number((num(v.cfsChargesNew) * count * 1.18 * 0.06) / cftVolume).toFixed(2);
      const chaSurveyVal = Number((num(v.chaSurveyNew) * count * 1.18 * 0.06) / cftVolume).toFixed(2);
      const freightChargeVal = Number((num(v.freightChargeNew) * count * 1.05 * 0.06) / cftVolume).toFixed(2);
      const unLoadingVal = Number((num(v.unloadingNew) * count * 1.18 * 0.06) / cftVolume).toFixed(2);
      const extraExpensesVal = Number((num(v.yardRentNew) * count * 1.18 * 0.06) / cftVolume).toFixed(2);
      
      const pqfumigationss = Number((num(v.pqFumigation) * 0.06) / cftVolume).toFixed(4);
      const pqweightss = Number((num(v.pqWeight) * 0.06) / cftVolume).toFixed(4);
      
      const examinationChargesVal = Number((num(v.examinationChargenew) * count * 1.18 * 0.06) / cftVolume).toFixed(2);
      const cfsGroundChargesVal = Number((num(v.cfsGroundRentNew) * count * 1.18 * 0.06) / cftVolume).toFixed(2);
      const sec49ChargesVal = Number((num(v.sec49New) * count * 1.18 * 0.06) / cftVolume).toFixed(2);

      const totalintval = [
        bgval, fronval, advisval, marginval, marginvalfive,
        customDutyval, socialWelfareChargeval, shippingBillval,
        pqWeightval, pqFumigationVal, fumigationChargeVal,
        cfsChargesVal, chaSurveyVal, freightChargeVal, unLoadingVal,
        extraExpensesVal, pqfumigationss, pqweightss,
        examinationChargesVal, cfsGroundChargesVal, sec49ChargesVal,
      ].reduce((sum, val) => sum + (Number(val) || 0), 0);

      return {
        inputs: [
          { label: 'totalNetCbm', val: fmt(netCbm) },
        ],
        substitutedFormula: `${bgval} + ${fronval} + ${advisval} + ${marginval} + ${marginvalfive} + ${customDutyval} + ${socialWelfareChargeval} + ${shippingBillval} + ${pqWeightval} + ${pqFumigationVal} + ${fumigationChargeVal} + ${cfsChargesVal} + ${chaSurveyVal} + ${freightChargeVal} + ${unLoadingVal} + ${extraExpensesVal} + ${pqfumigationss} + ${pqweightss} + ${examinationChargesVal} + ${cfsGroundChargesVal} + ${sec49ChargesVal}`,
        steps: [
          { label: '1. Bank Guarantee Charges Interest (bgval)', expr: bgval },
          { label: '2. Upfront Charges Interest (fronval)', expr: fronval },
          { label: '3. Advice Charges Interest (advisval)', expr: advisval },
          { label: '4. Margin 10% Interest (marginval)', expr: marginval },
          { label: '5. Margin 5% Interest (marginvalfive)', expr: marginvalfive },
          { label: '6. Custom Duty Interest (customDutyval)', expr: customDutyval },
          { label: '7. Social Welfare Charges Interest (socialWelfareChargeval)', expr: socialWelfareChargeval },
          { label: '8. Shipping Bill Interest (shippingBillval)', expr: shippingBillval },
          { label: '9. PQ Weight Check Interest (pqWeightval)', expr: pqWeightval },
          { label: '10. PQ Fumigation Relaxation Interest (pqFumigationVal)', expr: pqFumigationVal },
          { label: '11. Fumigation Charges Interest (fumigationChargeVal)', expr: fumigationChargeVal },
          { label: '12. CFS Charges Interest (cfsChargesVal)', expr: cfsChargesVal },
          { label: '13. CHA Survey Interest (chaSurveyVal)', expr: chaSurveyVal },
          { label: '14. Freight Charges Interest (freightChargeVal)', expr: freightChargeVal },
          { label: '15. Unloading Charges Interest (unLoadingVal)', expr: unLoadingVal },
          { label: '16. Yard Rent/Extra Exp Interest (extraExpensesVal)', expr: extraExpensesVal },
          { label: '17. PQ Fumigation Allocation Interest (pqfumigationss)', expr: pqfumigationss },
          { label: '18. PQ Weight 4x Allocation Interest (pqweightss)', expr: pqweightss },
          { label: '19. Examination Charges Interest (examinationChargesVal)', expr: examinationChargesVal },
          { label: '20. CFS Ground Rent Interest (cfsGroundChargesVal)', expr: cfsGroundChargesVal },
          { label: '21. Sec 49 Charges Interest (sec49ChargesVal)', expr: sec49ChargesVal },
          { label: 'Final Calculated Sum', expr: fmt(totalintval, 2) }
        ],
        liveOutput: fmt(totalintval, 2),
        outputLabel: 'Total Interest Allocation (totalInt)',
      };
    },
  },

  cost: {
    title: 'TOTAL COST',
    category: 'SHIP & CH',
    description: 'Total landed cost per unit = totalCft + od + agents + yardRent.',
    formulaText: 'cost = totalCft + od + agents + yardRent',
    evaluate: (v) => {
      const cft = num(v.totalCft);
      const od = num(v.od);
      const ag = num(v.agents) || 30;
      const yr = num(v.yardRent);
      const costVal = cft + od + ag + yr;

      return {
        inputs: [
          { label: 'totalCft', val: fmt(cft, 4) },
          { label: 'od (totalInt)', val: fmt(od, 4) },
          { label: 'agents', val: ag },
          { label: 'yardRent', val: fmt(yr, 4) },
        ],
        substitutedFormula: `${fmt(cft, 4)} + ${fmt(od, 4)} + ${ag} + ${fmt(yr, 4)} = ${fmt(costVal, 4)}`,
        steps: [
          { label: '1. Base CFT Rate (totalCft)', expr: `${fmt(cft, 4)}` },
          { label: '2. Overdraft Interest (od)', expr: `${fmt(od, 4)}` },
          { label: '3. Agent Allocation (agents)', expr: `${ag}` },
          { label: '4. Yard Rent Allocation', expr: `${fmt(yr, 4)}` },
          { label: 'Total Landed Cost', expr: `${fmt(cft, 4)} + ${fmt(od, 4)} + ${ag} + ${fmt(yr, 4)} = ${fmt(costVal, 4)}` },
        ],
        liveOutput: fmt(costVal, 4),
        outputLabel: 'Final Landed Cost Per Unit',
      };
    },
  },

  // ─── SUMMARY TAB ───
  roundOff: {
    title: 'ROUND OFF',
    category: 'SUMMARY',
    description: 'Adjustment amount subtracted from total amount to determine final invoice total.',
    formulaText: 'Total Invoice Amt = totalAmount - roundOff',
    evaluate: (v) => {
      const rows = v.dataEntryForm || [];
      const totAmt = rows.reduce((sum, row) => sum + num(row.netCbm) * num(row.avgRate), 0);
      const ro = num(v.roundOff);
      const netInv = totAmt - ro;

      return {
        inputs: [
          { label: 'totalAmount', val: fmt(totAmt) },
          { label: 'roundOff', val: ro },
        ],
        substitutedFormula: `${fmt(totAmt)} - ${ro} = ${fmt(netInv)}`,
        steps: [
          { label: 'Gross Summary Amount', expr: `${fmt(totAmt)}` },
          { label: 'Subtract Round Off', expr: `- ${ro}` },
          { label: 'Final Net Invoice', expr: `${fmt(netInv)}` },
        ],
        liveOutput: fmt(netInv),
        outputLabel: 'Total Invoice Amount',
      };
    },
  },
  totalAmount: {
    title: 'TOTAL AMOUNT',
    category: 'SUMMARY',
    description: 'Sum of all calculated summary row amounts (Net CBM × Average Rate per container row).',
    formulaText: 'totalAmount = sum(netCbm × avgRate) for all rows',
    evaluate: (v) => {
      const rows = v.dataEntryForm || [];
      const steps = rows.map((row, idx) => {
        const cNo = row.containerNo || `Row ${idx + 1}`;
        const nc = num(row.netCbm);
        const ar = num(row.avgRate);
        const rowAmt = nc * ar;
        return {
          label: `Row ${idx + 1} (${cNo})`,
          expr: `${fmt(nc)} Net CBM × ${fmt(ar)} Rate = ${fmt(rowAmt)}`
        };
      });
      const totAmt = rows.reduce((sum, row) => sum + num(row.netCbm) * num(row.avgRate), 0);
      return {
        inputs: [
          { label: 'Total Rows', val: rows.length }
        ],
        substitutedFormula: rows.map(r => `(${fmt(num(r.netCbm))} × ${fmt(num(r.avgRate))})`).join(' + ') || '0',
        steps: [
          ...steps,
          { label: 'Calculated Sum Total', expr: fmt(totAmt) }
        ],
        liveOutput: fmt(totAmt),
        outputLabel: 'Total Sum of Row Amounts (totalAmount)'
      };
    }
  },
  totalInvoiceAmt: {
    title: 'TOTAL INVOICE AMOUNT',
    category: 'SUMMARY',
    description: 'Final adjusted invoice total after subtracting Round Off from Total Amount.',
    formulaText: 'totalInvoiceAmt = totalAmount - roundOff',
    evaluate: (v) => {
      const rows = v.dataEntryForm || [];
      const totAmt = rows.reduce((sum, row) => sum + num(row.netCbm) * num(row.avgRate), 0);
      const ro = num(v.roundOff);
      const netInv = totAmt - ro;

      return {
        inputs: [
          { label: 'totalAmount', val: fmt(totAmt) },
          { label: 'roundOff', val: ro }
        ],
        substitutedFormula: `${fmt(totAmt)} - ${ro} = ${fmt(netInv)}`,
        steps: [
          { label: 'Gross Total Amount', expr: fmt(totAmt) },
          { label: 'Round Off Adjustment', expr: `- ${ro}` },
          { label: 'Final Net Invoice Amount', expr: fmt(netInv) }
        ],
        liveOutput: fmt(netInv),
        outputLabel: 'Net Total Invoice Amount'
      };
    }
  }
};
